import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import ApiService from 'redux/services/api-service';
import {addResultCases} from 'utils/helpers';
import {
  ListResult,
  MultiVariateAnalysisRun,
  MultiVariateAnalysisRunResult,
  PublishedMultiVariateAnalysisRun,
  SingleResult,
} from 'utils/types';

/**
 * Multivariate creative analysis runs, and the one of them (per sales lift run) a client sees.
 *
 * Staff read/list runs and their results off `/multivariate_analysis_runs/`; clients read the
 * published one off `/multivariate_analysis_run_published/`, which answers `{result: null}`
 * rather than 404 when nothing is published yet - same convention as RFP feasibility.
 *
 * Every endpoint is campaign-scoped: the permission mixin resolves the campaign off `campaign_id`
 * and rejects the request outright without it, so it goes on all four calls.
 */

export interface CombinationParams {
  itemGroup: string;
  responseVariable: string;
  zeroNegativeSales: boolean;
  removeOutliers: boolean;
}

type CampaignScoped = {campaignId: number | string};

const query = (campaignId: number | string, extra: Record<string, string> = {}): string =>
  new URLSearchParams({campaign_id: String(campaignId), ...extra}).toString();

const combinationQuery = (
  campaignId: number | string,
  {itemGroup, responseVariable, zeroNegativeSales, removeOutliers}: CombinationParams,
  extra: Record<string, string> = {},
): string =>
  query(campaignId, {
    item_group: itemGroup,
    response_variable: responseVariable,
    zero_negative_sales: String(zeroNegativeSales),
    remove_outliers: String(removeOutliers),
    ...extra,
  });

export interface IState {
  runs: ListResult<MultiVariateAnalysisRun>;
  results: SingleResult<MultiVariateAnalysisRunResult[]>;
  published: SingleResult<PublishedMultiVariateAnalysisRun | null>;
}

export const multiVariateAnalysisEndpoints = {
  listRuns: createAsyncThunk(
    'multiVariateAnalysis/listRuns',
    async (payload: CampaignScoped & {salesLiftAnalysisRunId: number | string}, {rejectWithValue}) => {
      try {
        const response = await ApiService.get(
          `/multivariate_analysis_runs/?${query(payload.campaignId, {
            sales_lift_analysis_run_id: String(payload.salesLiftAnalysisRunId),
          })}`,
        );
        return response.data;
      } catch (error: any) {
        return rejectWithValue(error.response?.data);
      }
    },
  ),
  getRunResults: createAsyncThunk(
    'multiVariateAnalysis/getRunResults',
    async (payload: CampaignScoped & {runId: number | string} & CombinationParams, {rejectWithValue}) => {
      const {campaignId, runId, ...combination} = payload;
      try {
        const response = await ApiService.get(
          `/multivariate_analysis_runs/${runId}/results/?${combinationQuery(campaignId, combination)}`,
        );
        return {value: (response.data ?? []) as MultiVariateAnalysisRunResult[]};
      } catch (error: any) {
        return rejectWithValue(error.response?.data);
      }
    },
  ),
  publishRun: createAsyncThunk(
    'multiVariateAnalysis/publishRun',
    async (payload: CampaignScoped & {id: number; published: boolean}, {rejectWithValue}) => {
      const {campaignId, id, published} = payload;
      try {
        const response = await ApiService.patch(`/multivariate_analysis_runs/${id}/publish/?${query(campaignId)}`, {
          published,
        });
        return response.data;
      } catch (error: any) {
        return rejectWithValue(error.response?.data);
      }
    },
  ),
  getPublished: createAsyncThunk(
    'multiVariateAnalysis/getPublished',
    async (
      payload: CampaignScoped & {salesLiftAnalysisRunId: number | string} & CombinationParams,
      {rejectWithValue},
    ) => {
      const {campaignId, salesLiftAnalysisRunId, ...combination} = payload;
      try {
        const response = await ApiService.get(
          `/multivariate_analysis_run_published/?${combinationQuery(campaignId, combination, {
            sales_lift_analysis_run_id: String(salesLiftAnalysisRunId),
          })}`,
        );
        // Nothing published yet is an ordinary state, so the API answers `{result: null}` rather
        // than 404. Unwrapped here so the page reads `value === null` and shows its empty state.
        return {value: response.data.result as PublishedMultiVariateAnalysisRun | null};
      } catch (error: any) {
        return rejectWithValue(error.response?.data);
      }
    },
  ),
};

export const multiVariateAnalysisSlice = createSlice({
  name: 'multiVariateAnalysis',
  initialState: {
    runs: {as: 'idle'},
    results: {as: 'idle'},
    published: {as: 'idle'},
  } as IState,
  reducers: {},
  extraReducers: builder => {
    addResultCases(builder, multiVariateAnalysisEndpoints.listRuns, 'runs');
    addResultCases(builder, multiVariateAnalysisEndpoints.getRunResults, 'results');
    addResultCases(builder, multiVariateAnalysisEndpoints.getPublished, 'published');
  },
});

export const multiVariateAnalysisActions = multiVariateAnalysisSlice.actions;

export type MultiVariateAnalysisSlice = {
  [multiVariateAnalysisSlice.name]: ReturnType<(typeof multiVariateAnalysisSlice)['reducer']>;
};
